<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import en from 'element-plus/es/locale/lang/en'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { Bot, LayoutDashboard, MessageSquareText, Radar as RadarIcon, Radio, Settings, UserRound, UsersRound } from 'lucide-vue-next'

import AuthModal from '@/components/AuthModal.vue'
import ToastStack from '@/components/ToastStack.vue'
import WebLogin from '@/components/WebLogin.vue'
import { setLocale } from '@/i18n'
import { useMonitorStore } from '@/stores/monitor'
import type {
  AnalysisSettings,
  ChannelConfig,
  GuestAccountCreate,
  GuestAccountUpdate,
  NotificationSettings,
} from '@/types/monitor'
import AnalysisView from '@/views/AnalysisView.vue'
import DashboardView from '@/views/DashboardView.vue'
import MessagesView from '@/views/MessagesView.vue'
import RadarView from '@/views/RadarView.vue'
import GuestAccountsView from '@/views/GuestAccountsView.vue'
import SettingsView from '@/views/SettingsView.vue'
import SourcesView from '@/views/SourcesView.vue'

type Workspace = 'overview' | 'messages' | 'analysis' | 'radar' | 'sources' | 'guests' | 'settings'

const store = useMonitorStore()
const { t, locale } = useI18n()
const {
  status,
  messages,
  messagesLoadingOlder,
  messagesHasMore,
  senderStats,
  analyses,
  alphaOverview,
  alphaLoading,
  intelligenceGraph,
  intelligenceLoading,
  channels,
  telegramDialogs,
  telegramDialogsLoading,
  capabilities,
  memoryStatus,
  marketStatus,
  marketEpisodes,
  onchainRadarItems,
  onchainRadarLoading,
  onchainRadarError,
  onchainStatus,
  onchainStatusLoading,
  onchainStatusError,
  onchainHolderHistory,
  onchainHolderHistoryLoading,
  onchainHolderHistoryError,
  onchainHolderHistoryWindowSeconds,
  onchainSmartFlow,
  onchainSmartFlowLoading,
  onchainSmartFlowError,
  onchainSmartFlowWindowSeconds,
  onchainCryptoGraph,
  onchainCryptoGraphLoading,
  onchainCryptoGraphError,
  onchainCryptoGraphWindowSeconds,
  onchainRadarStateFilter,
  onchainRadarRiskFilter,
  onchainRadarSourceFilter,
  onchainRadarTokenQuery,
  onchainRadarNextBefore,
  onchainRadarLoadedAt,
  selectedOnchainRadar,
  onchainWalletClusters,
  onchainWalletClustersLoading,
  onchainWalletClustersError,
  onchainEpisodeEvents,
  onchainEpisodeTimelineLoading,
  onchainEpisodeNextBefore,
  notices,
  loading,
  bootstrapError,
  websocketState,
  latestAnalysis,
  analysisSettings,
  notificationSettings,
  guestAccounts,
  isRealtime,
} = storeToRefs(store)

const activeWorkspace = ref<Workspace>('overview')
const authVisible = ref(false)
const webAuthChecking = ref(true)
const webAuthenticated = ref(false)
const webAuthPending = ref(false)
const webAuthError = ref<string | null>(null)
const webAuthRole = ref<'admin' | 'viewer'>('admin')
const sourceSaving = ref(false)
const analysisSaving = ref(false)
const notificationSaving = ref(false)
const guestSaving = ref(false)
const elementLocale = computed(() => (locale.value === 'zh-CN' ? zhCn : en))

const contentNavItems = computed(() => [
  { key: 'overview' as const, label: t('workspace.overview'), icon: LayoutDashboard },
  { key: 'messages' as const, label: t('workspace.messages'), icon: MessageSquareText },
  { key: 'analysis' as const, label: t('workspace.analysis'), icon: Bot },
  { key: 'radar' as const, label: t('workspace.radar'), icon: RadarIcon },
])
const adminNavItems = computed(() => [
  { key: 'sources' as const, label: t('workspace.sources'), icon: UsersRound },
  { key: 'guests' as const, label: t('workspace.guests'), icon: UserRound },
  { key: 'settings' as const, label: t('workspace.settings'), icon: Settings },
])
const canWrite = computed(() => webAuthRole.value === 'admin')

onMounted(() => void initializeWebSession())
onBeforeUnmount(() => store.dispose())

async function initialize(): Promise<void> {
  try {
    await store.bootstrap()
    store.connectRealtime()
    if (
      canWrite.value &&
      capabilities.value.telegram_enabled &&
      ['unauthorized', 'code_requested', 'password_required'].includes(status.value.authorization)
    ) {
      authVisible.value = true
    }
  } catch {
    // bootstrapError is rendered below.
  }
}

async function initializeWebSession(): Promise<void> {
  webAuthChecking.value = true
  webAuthError.value = null
  try {
    const response = await fetch('/api/v1/auth/session', { credentials: 'same-origin' })
    if (!response.ok) throw new Error(t('app.authCheckFailed', { status: response.status }))
    const session = (await response.json()) as { authenticated: boolean; role?: 'admin' | 'viewer' }
    webAuthenticated.value = session.authenticated
    webAuthRole.value = session.role ?? 'admin'
    if (session.authenticated) await initialize()
  } catch (error) {
    webAuthError.value = error instanceof Error ? error.message : String(error)
  } finally {
    webAuthChecking.value = false
  }
}

async function webLogin(username: string, password: string): Promise<void> {
  webAuthPending.value = true
  webAuthError.value = null
  try {
    const response = await fetch('/api/v1/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    })
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({}))) as { error?: { message?: string } }
      throw new Error(payload.error?.message ?? t('webLogin.loginFailed', { status: response.status }))
    }
    const session = (await response.json()) as { role?: 'admin' | 'viewer' }
    webAuthenticated.value = true
    webAuthRole.value = session.role ?? 'admin'
    await initialize()
  } catch (error) {
    webAuthError.value = error instanceof Error ? error.message : String(error)
  } finally {
    webAuthPending.value = false
  }
}

async function webLogout(): Promise<void> {
  try {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' })
  } finally {
    window.location.reload()
  }
}

function refreshRadarWorkspace(): void {
  void store.loadOnchainRadar(true).catch((error) => {
    store.pushNotice('warning', error instanceof Error ? error.message : String(error))
  })
  void store.loadOnchainStatus().catch((error) => {
    store.pushNotice('warning', error instanceof Error ? error.message : String(error))
  })
}

async function navigate(target: Workspace): Promise<void> {
  if (!canWrite.value && (target === 'sources' || target === 'guests' || target === 'settings')) return
  activeWorkspace.value = target
  if (target === 'analysis') {
    void store.loadAlphaOverview().catch((error) => {
      store.pushNotice('warning', error instanceof Error ? error.message : String(error))
    })
  }
  if (target === 'radar') {
    refreshRadarWorkspace()
  }
  if (target === 'guests') {
    void store.loadGuestAccounts().catch((error) => {
      store.pushNotice('error', error instanceof Error ? error.message : String(error))
    })
  }
  if ((target === 'sources' || target === 'settings') && status.value.authorization === 'authorized') {
    try {
      await store.loadTelegramDialogs()
    } catch (error) {
      store.pushNotice('error', error instanceof Error ? error.message : String(error))
    }
  }
}

async function reloadDialogs(): Promise<void> {
  try {
    await store.loadTelegramDialogs()
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  }
}

async function saveSources(nextChannels: ChannelConfig[]): Promise<void> {
  sourceSaving.value = true
  try {
    await store.saveChannelSettings(nextChannels)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    sourceSaving.value = false
  }
}

async function saveAnalysis(settings: AnalysisSettings): Promise<void> {
  analysisSaving.value = true
  try {
    await store.saveAnalysisSettings(settings)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    analysisSaving.value = false
  }
}

async function saveNotification(settings: NotificationSettings): Promise<void> {
  notificationSaving.value = true
  try {
    await store.saveNotificationSettings(settings)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    notificationSaving.value = false
  }
}

async function createGuest(account: GuestAccountCreate): Promise<void> {
  guestSaving.value = true
  try {
    await store.createGuestAccount(account)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    guestSaving.value = false
  }
}

async function updateGuest(id: string, account: GuestAccountUpdate): Promise<void> {
  guestSaving.value = true
  try {
    await store.updateGuestAccount(id, account)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    guestSaving.value = false
  }
}

async function deleteGuest(id: string): Promise<void> {
  guestSaving.value = true
  try {
    await store.deleteGuestAccount(id)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    guestSaving.value = false
  }
}

async function revokeGuestSessions(id: string): Promise<void> {
  guestSaving.value = true
  try {
    await store.revokeGuestAccountSessions(id)
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    guestSaving.value = false
  }
}

async function revokeAllGuestSessions(): Promise<void> {
  guestSaving.value = true
  try {
    await store.revokeAllGuestSessions()
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  } finally {
    guestSaving.value = false
  }
}

async function runAnalysis(): Promise<void> {
  try {
    await store.triggerAnalysis()
  } catch (error) {
    store.pushNotice('error', error instanceof Error ? error.message : String(error))
  }
}

function changeLocale(next: 'zh-CN' | 'en'): void {
  setLocale(next)
}
</script>

<template>
  <el-config-provider :locale="elementLocale">
    <div v-if="webAuthChecking" class="tm-startup">
      <div class="tm-startup-mark"><Radio :size="30" /></div>
      <h1>{{ t('app.checkingSession') }}</h1>
      <p>{{ t('app.checkingSessionDetail') }}</p>
    </div>

    <WebLogin
      v-else-if="!webAuthenticated"
      :pending="webAuthPending"
      :error="webAuthError"
      @login="webLogin"
    />

    <div v-else-if="loading" class="tm-startup">
      <div class="tm-startup-mark tm-startup-mark--pulse"><Radio :size="30" /></div>
      <h1>{{ t('app.establishing') }}</h1>
      <p>{{ t('app.establishingDetail') }}</p>
    </div>

    <div v-else-if="bootstrapError" class="tm-startup tm-startup--error">
      <h1>{{ t('app.unavailable') }}</h1>
      <p>{{ bootstrapError }}</p>
      <button class="tm-button tm-button--primary" @click="initialize">{{ t('common.retry') }}</button>
    </div>

    <div v-else class="tm-app-shell">
      <aside class="tm-sidebar">
        <div class="tm-brand">
          <div class="tm-brand-mark"><Radio :size="19" /></div>
          <div><strong>{{ t('workspace.brand') }}</strong><span>{{ t('workspace.subtitle') }}</span></div>
        </div>

        <nav :class="['tm-nav', { 'tm-nav--admin': canWrite }]">
          <span class="tm-nav-section-label">{{ t('workspace.contentSection') }}</span>
          <button
            v-for="item in contentNavItems"
            :key="item.key"
            :class="{ active: activeWorkspace === item.key }"
            @click="navigate(item.key)"
          >
            <component :is="item.icon" :size="19" />
            <span>{{ item.label }}</span>
          </button>
          <template v-if="canWrite">
            <span class="tm-nav-section-label tm-nav-section-label--admin">{{ t('workspace.adminSection') }}</span>
            <button
              v-for="item in adminNavItems"
              :key="item.key"
              :class="{ active: activeWorkspace === item.key }"
              @click="navigate(item.key)"
            >
              <component :is="item.icon" :size="19" />
              <span>{{ item.label }}</span>
            </button>
          </template>
        </nav>

        <div class="tm-sidebar-status">
          <div v-if="!canWrite" class="tm-viewer-badge">
            <span>{{ t('workspace.viewerMode') }}</span>
            <small>{{ t('workspace.viewerModeHint') }}</small>
          </div>
          <div><span :class="['tm-dot', { 'tm-dot--ok': isRealtime }]"></span><strong>{{ isRealtime ? t('workspace.online') : t('workspace.offline') }}</strong></div>
          <small>{{ channels.length }} {{ t('workspace.monitoredSources') }} · {{ websocketState }}</small>
          <button v-if="!canWrite" class="tm-sidebar-logout" @click="webLogout">{{ t('workspace.logout') }}</button>
        </div>
      </aside>

      <main class="tm-main">
        <DashboardView
          v-if="activeWorkspace === 'overview'"
          :status="status"
          :channels="channels"
          :messages="messages"
          :latest-analysis="latestAnalysis"
          :analysis-settings="analysisSettings"
          :market-status="marketStatus"
          :market-episodes="marketEpisodes"
          :realtime="isRealtime"
          :can-write="canWrite"
          @navigate="navigate"
          @trigger-analysis="runAnalysis"
        />
        <MessagesView
          v-else-if="activeWorkspace === 'messages'"
          :messages="messages"
          :channels="channels"
          :sender-stats="senderStats"
          :realtime="isRealtime"
          :loading-older="messagesLoadingOlder"
          :has-more="messagesHasMore"
          @load-older="store.loadOlderMessages"
          @sender-filter="store.setMessageSenderFilter"
        />
        <AnalysisView
          v-else-if="activeWorkspace === 'analysis'"
          :status="status"
          :analyses="analyses"
          :alpha-overview="alphaOverview"
          :alpha-loading="alphaLoading"
          :intelligence-graph="intelligenceGraph"
          :intelligence-loading="intelligenceLoading"
          :can-write="canWrite"
          @trigger="runAnalysis"
        />
        <RadarView
          v-else-if="activeWorkspace === 'radar'"
          :items="onchainRadarItems"
          :loading="onchainRadarLoading"
          :error="onchainRadarError"
          :runtime-status="onchainStatus"
          :runtime-status-loading="onchainStatusLoading"
          :runtime-status-error="onchainStatusError"
          :holder-history="onchainHolderHistory"
          :holder-history-loading="onchainHolderHistoryLoading"
          :holder-history-error="onchainHolderHistoryError"
          :holder-history-window-seconds="onchainHolderHistoryWindowSeconds"
          :smart-flow="onchainSmartFlow"
          :smart-flow-loading="onchainSmartFlowLoading"
          :smart-flow-error="onchainSmartFlowError"
          :smart-flow-window-seconds="onchainSmartFlowWindowSeconds"
          :crypto-graph="onchainCryptoGraph"
          :crypto-graph-loading="onchainCryptoGraphLoading"
          :crypto-graph-error="onchainCryptoGraphError"
          :crypto-graph-window-seconds="onchainCryptoGraphWindowSeconds"
          :state-filter="onchainRadarStateFilter"
          :risk-filter="onchainRadarRiskFilter"
          :source-filter="onchainRadarSourceFilter"
          :token-query="onchainRadarTokenQuery"
          :has-more="Boolean(onchainRadarNextBefore)"
          :loaded-at="onchainRadarLoadedAt"
          :selected="selectedOnchainRadar"
          :clusters="onchainWalletClusters"
          :clusters-loading="onchainWalletClustersLoading"
          :clusters-error="onchainWalletClustersError"
          :timeline="onchainEpisodeEvents"
          :timeline-loading="onchainEpisodeTimelineLoading"
          :timeline-has-more="Boolean(onchainEpisodeNextBefore)"
          :capabilities="capabilities"
          @refresh="refreshRadarWorkspace"
          @filter="store.setOnchainRadarFilters"
          @load-more="store.loadMoreOnchainRadar"
          @select="store.selectOnchainRadar"
          @close-detail="store.clearOnchainRadarSelection"
          @holder-window="store.setOnchainHolderHistoryWindow"
          @smart-flow-window="store.setOnchainSmartFlowWindow"
          @crypto-graph-window="store.setOnchainCryptoGraphWindow"
          @load-more-events="store.loadMoreOnchainEpisodeEvents"
        />
        <SourcesView
          v-else-if="activeWorkspace === 'sources'"
          :dialogs="telegramDialogs"
          :channels="channels"
          :loading="telegramDialogsLoading"
          :saving="sourceSaving"
          :authorization="status.authorization"
          :telegram-enabled="capabilities.telegram_enabled"
          @reload="reloadDialogs"
          @save="saveSources"
          @authenticate="authVisible = true"
        />
        <GuestAccountsView
          v-else-if="activeWorkspace === 'guests'"
          :accounts="guestAccounts"
          :busy="guestSaving"
          @refresh="store.loadGuestAccounts"
          @create="createGuest"
          @update="updateGuest"
          @delete="deleteGuest"
          @revoke-sessions="revokeGuestSessions"
          @revoke-all-sessions="revokeAllGuestSessions"
        />
        <SettingsView
          v-else
          :analysis="analysisSettings"
          :status="status"
          :analysis-saving="analysisSaving"
          :notification="notificationSettings"
          :dialogs="telegramDialogs"
          :saving="notificationSaving"
          :memory="memoryStatus"
          @save-analysis="saveAnalysis"
          @save-notification="saveNotification"
          @refresh-memory="store.refreshMemoryStatus"
          @locale="changeLocale"
          @logout="webLogout"
        />
      </main>

      <AuthModal
        :open="authVisible"
        :authorization="status.authorization"
        default-phone=""
        @close="authVisible = false"
      />
      <ToastStack :notices="notices" @dismiss="store.dismissNotice" />
    </div>
  </el-config-provider>
</template>
